'use client';

import Link from 'next/link';
import { type ReactNode, type MouseEvent } from 'react';
import {
    type PageContext,
    type CtaContext,
    trackCtaClick,
    buildTrackedCTAUrl,
} from '@/lib/cta-tracking';

interface TrackedCTAProps {
    /** Target URL (external URLs navigate via window.location) */
    href: string;
    /** Page context from the containing page */
    pageContext: PageContext;
    /** CTA metadata for tracking */
    ctaContext: CtaContext;
    /** Button/link content */
    children: ReactNode;
    /** CSS classes */
    className?: string;
}

/**
 * A tracked CTA link that automatically:
 * 1. Fires a cta_clicked event on click
 * 2. Builds a tracked URL with attribution + page/CTA context params
 *
 * Use this as a drop-in replacement for <Link> or <a> on CTA buttons.
 */
export function TrackedCTA({
    href,
    pageContext,
    ctaContext,
    children,
    className,
}: TrackedCTAProps) {
    const isExternal = href.startsWith('http');

    // For external links, build the tracked URL at click time (not render time)
    // so visitor/session IDs are available even on first page load.
    const handleExternalClick = (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        trackCtaClick(pageContext, ctaContext);
        const trackedHref = buildTrackedCTAUrl(href, pageContext, ctaContext);
        window.location.href = trackedHref;
    };

    const handleInternalClick = () => {
        trackCtaClick(pageContext, ctaContext);
    };

    if (isExternal) {
        return (
            <a
                href={href}
                onClick={handleExternalClick}
                className={className}
                rel="noopener"
            >
                {children}
            </a>
        );
    }

    // Internal links use Next.js Link
    return (
        <Link href={href} onClick={handleInternalClick} className={className}>
            {children}
        </Link>
    );
}
